<template>
  <el-dialog
    title="Card Creation Statistics"
    :visible.sync="dialogVisible"
    width="800px"
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <div class="statistics-container">
      <!-- Time Period Selector -->
      <div class="period-selector">
        <el-radio-group v-model="selectedPeriod" @change="handlePeriodChange">
          <el-radio-button label="days">Last 12 Days</el-radio-button>
          <el-radio-button label="weeks">Last 12 Weeks</el-radio-button>
          <el-radio-button label="months">Last 12 Months</el-radio-button>
        </el-radio-group>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-container">
        <el-skeleton :rows="6" animated />
      </div>

      <!-- Chart Container -->
      <div v-else class="chart-container">
        <canvas ref="chartCanvas"></canvas>
      </div>

      <!-- Summary Statistics -->
      <div v-if="!loading && statistics" class="summary-stats">
        <div class="stat-item">
          <div class="stat-label">Total Cards</div>
          <div class="stat-value">{{ totalCards }}</div>
        </div>
        <div class="stat-item">
          <div class="stat-label">Average per {{ periodLabel }}</div>
          <div class="stat-value">{{ averageCards }}</div>
        </div>
        <div class="stat-item">
          <div class="stat-label">Most Productive {{ periodLabel }}</div>
          <div class="stat-value">{{ mostProductivePeriod }}</div>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import Chart from 'chart.js/dist/Chart.js'
import { postRequest } from '@/apis/front'
import dayjs from 'dayjs'

export default {
  name: 'CardStatisticsDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dialogVisible: false,
      selectedPeriod: 'days',
      loading: false,
      statistics: null,
      chart: null
    }
  },
  computed: {
    periodLabel() {
      const labels = {
        'days': 'Day',
        'weeks': 'Week',
        'months': 'Month'
      }
      return labels[this.selectedPeriod] || 'Period'
    },
    totalCards() {
      if (!this.statistics || !this.statistics.data) return 0
      return this.statistics.data.reduce((sum, item) => sum + item.count, 0)
    },
    averageCards() {
      if (!this.statistics || !this.statistics.data || this.statistics.data.length === 0) return 0
      const total = this.totalCards
      const periods = this.statistics.data.length
      return (total / periods).toFixed(1)
    },
    mostProductivePeriod() {
      if (!this.statistics || !this.statistics.data || this.statistics.data.length === 0) return '-'
      const maxItem = this.statistics.data.reduce((max, item) => 
        item.count > max.count ? item : max
      , this.statistics.data[0])
      return maxItem.label
    }
  },
  watch: {
    visible(newVal) {
      this.dialogVisible = newVal
      if (newVal) {
        // Delay loading to ensure dialog is fully rendered
        this.$nextTick(() => {
          setTimeout(() => {
            this.loadStatistics()
          }, 100)
        })
      }
    },
    dialogVisible(newVal) {
      if (!newVal) {
        this.$emit('update:visible', false)
      }
    }
  },
  methods: {
    async loadStatistics() {
      this.loading = true
      try {
        const response = await postRequest('getCardStatistics', {
          user_id: this.$store.state.user_id,
          period_type: this.selectedPeriod,
          period_count: 12
        })

        if (response.data.code === 0) {
          this.statistics = this.processStatisticsData(response.data.data || {})
          // Add a small delay to ensure dialog is fully rendered
          setTimeout(() => {
            this.renderChart()
          }, 200)
        } else {
          this.$message.error(response.data.msg || 'Failed to load statistics')
        }
      } catch (error) {
        console.error('Error loading statistics:', error)
        this.$message.error('Failed to load statistics')
      } finally {
        this.loading = false
      }
    },

    processStatisticsData(rawData) {
      const data = []
      
      // rawData is an object with date keys and count values
      // Convert it to an array and sort by date
      const sortedEntries = Object.entries(rawData).sort((a, b) => a[0].localeCompare(b[0]))
      
      sortedEntries.forEach(([dateKey, count]) => {
        let label
        
        if (this.selectedPeriod === 'days') {
          // dateKey format: YYYY-MM-DD
          const date = dayjs(dateKey)
          label = date.format('MMM DD')
        } else if (this.selectedPeriod === 'weeks') {
          // dateKey format: YYYY-MM-DD (Monday of the week)
          const date = dayjs(dateKey)
          label = `Week of ${date.format('MMM DD')}`
        } else if (this.selectedPeriod === 'months') {
          // dateKey format: YYYY-MM
          const date = dayjs(dateKey + '-01') // Add day for parsing
          label = date.format('MMM YYYY')
        }
        
        data.push({
          label,
          dateKey,
          count
        })
      })

      return { data }
    },

    renderChart() {
      if (this.chart) {
        this.chart.destroy()
        this.chart = null
      }

      if (!this.statistics || !this.statistics.data) return

      // Wait for next tick to ensure DOM is ready
      this.$nextTick(() => {
        const canvas = this.$refs.chartCanvas
        if (!canvas) {
          console.error('Canvas element not found')
          return
        }

        // Set canvas dimensions explicitly
        const container = canvas.parentElement
        if (container) {
          canvas.width = container.offsetWidth
          canvas.height = 400
        }

        const ctx = canvas.getContext('2d')
        
        this.chart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: this.statistics.data.map(item => item.label),
          datasets: [{
            label: 'Cards Created',
            data: this.statistics.data.map(item => item.count),
            backgroundColor: 'rgba(64, 158, 255, 0.6)',
            borderColor: 'rgba(64, 158, 255, 1)',
            borderWidth: 1,
            barPercentage: 0.7
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            display: false
          },
          tooltips: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            titleFontColor: '#fff',
            bodyFontColor: '#fff',
            borderColor: 'rgba(64, 158, 255, 0.5)',
            borderWidth: 1,
            xPadding: 12,
            yPadding: 12,
            displayColors: false,
            callbacks: {
              label: function(tooltipItem) {
                return `Cards Created: ${tooltipItem.yLabel}`
              }
            }
          },
          scales: {
            xAxes: [{
              gridLines: {
                display: false
              },
              ticks: {
                fontColor: '#606266',
                fontSize: 12
              }
            }],
            yAxes: [{
              gridLines: {
                color: 'rgba(0, 0, 0, 0.05)',
                drawBorder: false
              },
              ticks: {
                beginAtZero: true,
                stepSize: 1,
                fontColor: '#606266',
                fontSize: 12,
                callback: function(value) {
                  if (value % 1 === 0) {
                    return value
                  }
                }
              }
            }]
          }
        }
      })
      })
    },

    handlePeriodChange() {
      this.loadStatistics()
    },

    handleClose() {
      if (this.chart) {
        this.chart.destroy()
        this.chart = null
      }
    }
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.destroy()
    }
  }
}
</script>

<style scoped>
.statistics-container {
  padding: 20px 0;
}

.period-selector {
  text-align: center;
  margin-bottom: 30px;
}

.loading-container {
  min-height: 400px;
  padding: 20px;
}

.chart-container {
  height: 400px;
  padding: 0 20px;
  margin-bottom: 30px;
}

.summary-stats {
  display: flex;
  justify-content: space-around;
  padding: 20px;
  background: #f5f7fa;
  border-radius: 8px;
  margin-top: 20px;
}

.stat-item {
  text-align: center;
}

.stat-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-value {
  font-size: 24px;
  font-weight: 600;
  color: #303133;
}

:deep(.el-radio-button__inner) {
  padding: 8px 20px;
}

:deep(.el-dialog__body) {
  padding: 10px 20px 30px;
}
</style>